"use client";

import NextImage from "next/image";
import { useNextSanityImage, UseNextSanityImageBuilder } from "next-sanity-image";

import { getClient } from "@/lib/sanityClient";
import cx from "classnames";

const DEFAULT_FALLBACK_IMAGE_QUALITY = 85;

const ARBITRARY_DEFAULT_WIDTH_HEIGHT = 1200;

const DEFAULT_IMAGE_BUILDER =
  (params) =>
  (imageUrlBuilder, options): UseNextSanityImageBuilder => {
    // console.log({ params, options });

    // https://www.sanity.io/docs/image-urls

    const result = imageUrlBuilder
      .quality(options.quality ?? DEFAULT_FALLBACK_IMAGE_QUALITY)
      .format(params.format || "webp")
      .saturation(params.saturation || undefined)
      .fit(params.fit || "clip")
      .crop(params.crop || "center")
      .width(params.width || Math.min(options.originalImageDimensions.width, 1920))
      .height(params.height || options.originalImageDimensions.width);

    return result;
  };

const Image = ({
  src,
  className = "",
  replaceClassName = false,
  imageBuilder = undefined,
  ...props
}) => {
  // if (!src) {
  //   throw Error("No src provided");
  // }

  // console.log(src);

  // https://nextjs.org/docs/pages/api-reference/next-config-js/images#sanity
  // https://github.com/lorenzodejong/next-sanity-image#readme
  const imageProps = useNextSanityImage(
    getClient(),
    typeof src === "string" || src instanceof String ? null : src,
    {
      imageBuilder: imageBuilder || DEFAULT_IMAGE_BUILDER(props),
    },
  );

  const { width, height, ...moreProps } = { ...imageProps };

  if (
    (typeof src === "string" || src instanceof String) &&
    (src.startsWith("http") || src.startsWith("/"))
  ) {
    return (
      <NextImage
        src={src}
        // className={className}
        className={cx({ "w-full h-auto object-cover": !replaceClassName }, className)}
        // style={{ width: "100%" }} // layout="responsive" prior to Next 13.0.0
        sizes="(max-width: 800px) 100vw, 800px"
        alt=""
        // style={{ width: "100%", height: "auto" }}
        {...props}
        width={props.fill ? undefined : (width ?? props.width ?? ARBITRARY_DEFAULT_WIDTH_HEIGHT)}
        height={props.fill ? undefined : (height ?? props.height ?? ARBITRARY_DEFAULT_WIDTH_HEIGHT)}
        {...moreProps}
      />
    );
  }

  return (
    // https://beta.nextjs.org/docs/api-reference/components/image
    <NextImage
      {...imageProps}
      className={cx(
        {
          "w-full h-auto object-cover": !replaceClassName,
        },
        className,
      )}
      // style={{ width: "100%", height: "auto" }} // layout="responsive" prior to Next 13.0.0
      sizes="(max-width: 800px) 100vw, 800px"
      alt=""
      {...props}
      width={props.fill ? undefined : (width ?? ARBITRARY_DEFAULT_WIDTH_HEIGHT)}
      height={props.fill ? undefined : (height ?? ARBITRARY_DEFAULT_WIDTH_HEIGHT)}
      {...moreProps}
    />
  );
};

export default Image;
